import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Loader2, ArrowUp, ArrowDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveImg } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import ImageUpload from "./ImageUpload";

const EMPTY = { name_ar: "", name_en: "", desc_ar: "", desc_en: "", category: "massage", duration: 60, price: 0, image_url: "", active: true, restrictions_ar: "", restrictions_en: "" };

export default function ServicesManage() {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/services?all=true"), api.get("/categories")])
      .then(([s, c]) => { setServices(s.data); setCategories(c.data); })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/services/${data.id}`, data);
      else await api.post("/admin/services", { ...data, order: services.length });
      toast.success("تم الحفظ"); setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("حذف هذه الخدمة؟")) return;
    await api.delete(`/admin/services/${id}`); toast.success("تم الحذف"); load();
  };

  const move = async (idx, dir) => {
    const arr = [...services];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setServices(arr);
    await api.post("/admin/services/reorder", { ids: arr.map((s) => s.id) });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">إدارة الخدمات</h1>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full" data-testid="add-service-btn"><Plus className="h-4 w-4 me-1" /> خدمة جديدة</Button>
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <div key={s.id} className="rounded-2xl bg-card cb-card-shadow overflow-hidden" data-testid={`admin-service-${s.id}`}>
              <div className="relative aspect-video bg-muted">
                <img src={resolveImg(s.image_url)} alt="" className="h-full w-full object-cover" />
                {!s.active && <span className="absolute top-2 start-2 px-2 py-0.5 rounded-full bg-rose-500 text-white text-xs">معطّلة</span>}
              </div>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{s.name_ar}</h3>
                  <span className="text-primary font-bold">{s.price} ر.س</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{s.duration} دقيقة · {categories.find((c) => c.id === s.category)?.name_ar}</p>
                <div className="flex gap-1 mt-3">
                  <Button size="sm" variant="outline" onClick={() => setEditing(s)} className="rounded-full border-2 flex-1" data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => move(i, -1)}><ArrowUp className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => move(i, 1)}><ArrowDown className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => del(s.id)} className="text-destructive" data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ServiceDialog editing={editing} categories={categories} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}

function ServiceDialog({ editing, categories, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [genBusy, setGenBusy] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!editing) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.name_ar.trim()) { toast.error("أدخلي اسم الخدمة أولاً"); return; }
    setGenBusy(true);
    try {
      const catName = categories.find((c) => c.id === form.category)?.name_ar || "";
      const { data } = await api.post("/admin/ai/generate-description", { name: form.name_ar, category: catName, kind: "service" });
      setForm((f) => ({ ...f, desc_ar: data.ar || f.desc_ar, desc_en: data.en || f.desc_en }));
      toast.success("تم توليد الوصف");
    } catch (e) { toast.error(apiErr(e)); } finally { setGenBusy(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle>{form.id ? "تعديل خدمة" : "خدمة جديدة"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1 block text-xs">الاسم (عربي)</Label><Input value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} className="rounded-xl h-11" data-testid="svc-name-ar" /></div>
            <div><Label className="mb-1 block text-xs">الاسم (إنجليزي)</Label><Input value={form.name_en} onChange={(e) => set("name_en", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
          </div>
          <div className="flex justify-end">
            <Button type="button" size="sm" variant="outline" onClick={generate} disabled={genBusy} className="rounded-full border-2 text-primary" data-testid="svc-ai-generate">
              {genBusy ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Sparkles className="h-4 w-4 me-1" />} اكتبي الوصف بالذكاء الاصطناعي
            </Button>
          </div>
          <div><Label className="mb-1 block text-xs">الوصف (عربي)</Label><Textarea value={form.desc_ar} onChange={(e) => set("desc_ar", e.target.value)} className="rounded-xl" rows={2} /></div>
          <div><Label className="mb-1 block text-xs">الوصف (إنجليزي)</Label><Textarea value={form.desc_en} onChange={(e) => set("desc_en", e.target.value)} className="rounded-xl" rows={2} dir="ltr" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="mb-1 block text-xs">التصنيف</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="svc-category"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_ar}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1 block text-xs">المدة (دقيقة)</Label><Input type="number" value={form.duration} onChange={(e) => set("duration", +e.target.value)} className="rounded-xl h-11" data-testid="svc-duration" /></div>
            <div><Label className="mb-1 block text-xs">السعر (ر.س)</Label><Input type="number" value={form.price} onChange={(e) => set("price", +e.target.value)} className="rounded-xl h-11" data-testid="svc-price" /></div>
          </div>
          <ImageUpload value={form.image_url} onChange={(v) => set("image_url", v)} label="صورة الخدمة" />
          <div><Label className="mb-1 block text-xs">قيود الحجز (عربي، اختياري)</Label><Input value={form.restrictions_ar} onChange={(e) => set("restrictions_ar", e.target.value)} className="rounded-xl h-11" /></div>
          <label className="flex items-center gap-3"><Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="svc-active" /> <span className="text-sm">الخدمة متاحة للحجز</span></label>
          <Button onClick={() => onSave(form)} className="rounded-full w-full h-11 font-semibold" data-testid="svc-save-btn">حفظ</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
